import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Camera, FileText, User, Menu } from "lucide-react";
import Logo from "@/components/Logo";
import OnboardingTour from "@/components/OnboardingTour";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useApp } from "@/state/AppContext";

const tabs = [
  { key: "home", label: "Start", icon: Home, to: "/" },
  { key: "check", label: "Prüfen", icon: Camera, to: "/upload" },
  { key: "letters", label: "Meine Briefe", icon: FileText, to: "/letters" },
  { key: "profile", label: "Profil", icon: User, to: "/profile" },
];

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setToken, loyaltyActive } = useApp();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const go = (path) => { setMenuOpen(false); navigate(path); };

  const activeKey = (() => {
    const p = location.pathname;
    if (p === "/") return "home";
    if (p.startsWith("/upload") || p.startsWith("/analyzing") || p.startsWith("/result")
      || p.startsWith("/translation") || p.startsWith("/actions") || p.startsWith("/reply")) return "check";
    if (p.startsWith("/letters")) return "letters";
    if (p.startsWith("/profile") || p.startsWith("/auth") || p.startsWith("/pricing")) return "profile";
    return null;
  })();

  return (
    <div className="min-h-[100dvh] w-full flex justify-center">
      <div
        className="w-full max-w-[520px] min-h-[100dvh] bg-white relative flex flex-col sm:border-x sm:border-slate-200 sm:shadow-2xl"
        data-testid="app-container"
      >
        {/* Top header */}
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="header-logo-btn" className="active:scale-95 transition-transform">
            <Logo loyalty={loyaltyActive} />
          </button>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="header-menu-btn" className="p-2 rounded-xl hover:bg-blue-50 active:scale-95 transition">
                <Menu className="w-6 h-6 text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader><SheetTitle>Menü</SheetTitle></SheetHeader>
              <nav className="mt-6 flex flex-col gap-1">
                <MenuLink onClick={() => go("/")} testid="menu-home">Startseite</MenuLink>
                <MenuLink onClick={() => go("/upload")} testid="menu-upload">Brief prüfen</MenuLink>
                <MenuLink onClick={() => go("/letters")} testid="menu-letters">Meine Briefe</MenuLink>
                <MenuLink onClick={() => go("/pricing")} testid="menu-pricing">Preise</MenuLink>
                <MenuLink onClick={() => go("/profile")} testid="menu-profile">Profil</MenuLink>
                <MenuLink onClick={() => go("/impressum")} testid="menu-impressum">Impressum</MenuLink>
                <MenuLink onClick={() => go("/agb")} testid="menu-agb">AGB</MenuLink>
                <MenuLink onClick={() => go("/datenschutz")} testid="menu-datenschutz">Datenschutz</MenuLink>
                {user ? (
                  <button
                    onClick={() => { setMenuOpen(false); setToken(""); navigate("/"); }}
                    data-testid="menu-logout-btn"
                    className="text-left px-3 py-3 rounded-xl hover:bg-blue-50 text-slate-800"
                  >Abmelden</button>
                ) : (
                  <MenuLink onClick={() => go("/auth")} testid="menu-auth">Anmelden</MenuLink>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 app-safe-bottom hide-scrollbar" data-testid="app-main">
          <Outlet />
        </main>

        <OnboardingTour />

        {/* Bottom navigation — fixed to viewport so it never scrolls with content */}
        <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[520px] z-50 bg-white/95 backdrop-blur-md border-t border-slate-100 pt-2 grid grid-cols-4 app-nav-safe"
             data-testid="bottom-nav">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = activeKey === t.key;
            return (
              <button
                key={t.key}
                onClick={() => navigate(t.to)}
                data-testid={`nav-${t.key}-btn`}
                className={`flex flex-col items-center justify-center gap-1 transition-colors ${active ? "text-blue-600" : "text-slate-500"}`}
              >
                <Icon className="w-6 h-6" strokeWidth={active ? 2.4 : 1.8} />
                <span className={`text-[11px] ${active ? "font-semibold" : "font-medium"}`}>{t.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

function MenuLink({ children, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left px-3 py-3 rounded-xl hover:bg-blue-50 text-slate-800">
      {children}
    </button>
  );
}
